import { Component } from '@theme/component';
import { prefersReducedMotion, throttle } from '@theme/utilities';

/**
 * Card state enum
 * @typedef {'locked' | 'unlocked' | 'revealed'} CardState
 */

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} cardsGrid - The cards grid/fan container
 * @property {HTMLElement[]} cards - Array of card elements
 * @property {HTMLFormElement} form - The form element
 * @property {HTMLInputElement} nameInput - Name input field
 * @property {HTMLInputElement} emailInput - Email input field
 * @property {HTMLButtonElement} submitButton - Submit button
 * @property {HTMLElement} cursorHint - The cursor hint element
 * @property {HTMLElement} result - The result section
 * @property {HTMLAnchorElement} ctaButton - CTA button element
 * @property {HTMLElement} liveRegion - ARIA live region for announcements
 */

/**
 * Mystery Prize Cards component
 * Interactive prize card section with shuffle animations and form integration
 * @extends {Component<Refs>}
 */
export class MysteryPrizeCards extends Component {
    requiredRefs = ['cardsGrid', 'form'];

    /** @type {CardState} */
    #state = 'locked';

    /** @type {number[]} */
    #cardOrder = [];

    /** @type {IntersectionObserver | null} */
    #scrollObserver = null;

    /** @type {number} */
    #lastScrollY = 0;

    /** @type {string} */
    #selectedLink = '';

    /** @type {boolean} */
    #isShuffling = false;

    /** @type {number} */
    #shuffleDirection = 1;

    /** @type {AbortController | null} */
    #abortController = null;

    /**
     * Called when the element is connected to the DOM
     */
    connectedCallback() {
        super.connectedCallback();

        this.#abortController = new AbortController();
        this.#initializeCardOrder();
        this.#setupScrollObserver();
        this.#bindEvents();
    }

    /**
     * Called when the element is disconnected from the DOM
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        this.#scrollObserver?.disconnect();
        this.#abortController?.abort();
        window.removeEventListener('scroll', this.#throttledScrollHandler);
    }

    /**
     * Initialize the card order array
     */
    #initializeCardOrder() {
        const cardCount = this.refs.cards?.length ?? 0;
        this.#cardOrder = Array.from({ length: cardCount }, (_, i) => i);
    }

    /**
     * Setup IntersectionObserver for scroll detection
     */
    #setupScrollObserver() {
        this.#scrollObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        this.#enableScrollShuffle();
                    } else {
                        this.#disableScrollShuffle();
                    }
                }
            },
            { threshold: 0.2 }
        );

        this.#scrollObserver.observe(this);
    }

    /**
     * One-time shuffle when entering view
     */
    #hasShuffledOnView = false;
    #enableScrollShuffle() {
        if (!this.#hasShuffledOnView && this.#state === 'locked') {
            this.#hasShuffledOnView = true;
            this.#shuffleCardsSequential();
        }
        this.#lastScrollY = window.scrollY;
        window.addEventListener('scroll', this.#throttledScrollHandler, {
            passive: true,
            signal: this.#abortController?.signal,
        });
    }

    /**
     * Disable scroll-based card shuffling
     */
    #disableScrollShuffle() {
        window.removeEventListener('scroll', this.#throttledScrollHandler);
    }

    /**
     * Throttled scroll handler for card shuffling
     */
    #throttledScrollHandler = throttle(() => {
        this.#handleScroll();
    }, 200);

    /**
     * Handle scroll event
     */
    #handleScroll() {
        // Only shuffle in locked state
        if (this.#state !== 'locked') return;

        // Skip if already shuffling
        if (this.#isShuffling) return;

        // Skip if user prefers reduced motion
        if (prefersReducedMotion()) return;

        const currentScrollY = window.scrollY;
        const scrollDelta = currentScrollY - this.#lastScrollY;

        // Determine shuffle direction based on scroll direction
        if (Math.abs(scrollDelta) > 50) {
            this.#shuffleDirection = scrollDelta > 0 ? 1 : -1;
            this.#shuffleCardsSequential();
            this.#lastScrollY = currentScrollY;
        }
    }

    /**
     * Shuffle cards sequentially like a deck being spread
     * @returns {Promise<void>}
     */
    async #shuffleCardsSequential() {
        const { cards } = this.refs;
        if (!cards || this.#isShuffling) return;

        this.#isShuffling = true;

        // 1. Gather cards to center/deck
        for (const card of cards) {
            card.classList.add('mystery-prize-cards__card--gathering');
        }
        await this.#wait(400);

        // 2. "Deck Shuffle" / Mix simulation
        // We visually jiggle and swap z-index slightly to simulate mixing
        const shuffleSteps = 6;
        for (let i = 0; i < shuffleSteps; i++) {
            for (const card of cards) {
                // Random small offset
                const randomX = (Math.random() - 0.5) * 10;
                const randomY = (Math.random() - 0.5) * 10;
                const randomRot = (Math.random() - 0.5) * 5;
                card.style.transform = `translateX(${randomX}px) translateY(${randomY}px) rotate(${randomRot}deg)`;
            }
            await this.#wait(100);
        }

        // Reset inline transforms from shuffle
        for (const card of cards) {
            card.style.transform = '';
        }

        // 3. Spread back to fan
        for (const card of cards) {
            card.classList.remove('mystery-prize-cards__card--gathering');
        }

        // Wait for spread animation
        await this.#wait(400);

        this.#isShuffling = false;
    }

    /**
     * Bind event listeners
     */
    #bindEvents() {
        const { form, cards } = this.refs;
        const signal = this.#abortController?.signal;

        // Form submission
        if (form) {
            form.addEventListener('submit', (e) => this.handleSubmit(e), { signal });
        }

        // Card clicks - add keyboard support
        if (cards) {
            for (const [index, card] of cards.entries()) {
                card.addEventListener(
                    'click',
                    (e) => this.#onCardClick(index, e),
                    { signal }
                );

                card.addEventListener(
                    'keydown',
                    (e) => this.#onCardKeydown(index, e),
                    { signal }
                );
            }
        }

        // Mouse movement for cursor tracking
        this.addEventListener('mousemove', (e) => this.#onMouseMove(e), { signal, passive: true });
    }

    /**
     * Handle card click
     * @param {number} index
     * @param {MouseEvent} event
     */
    #onCardClick(index, event) {
        if (this.#state !== 'unlocked') return;

        event.preventDefault();
        this.#selectCard(index);
    }

    /**
     * Handle card keydown for keyboard navigation
     * @param {number} index
     * @param {KeyboardEvent} event
     */
    #onCardKeydown(index, event) {
        if (this.#state !== 'unlocked') return;

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.#selectCard(index);
        }
    }

    /**
     * Handle form submission
     * @param {SubmitEvent} event
     */
    async handleSubmit(event) {
        event.preventDefault();

        const { form, nameInput, emailInput, submitButton } = this.refs;
        if (!form || !nameInput || !emailInput) return;

        // HTML5 validation
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        try {
            // Set loading state
            if (submitButton) {
                submitButton.disabled = true;
                submitButton.classList.add('mystery-prize-cards__submit--loading');
            }

            // Submit form data to backend
            await this.#submitFormData();

            // Trigger Shuffle before unlocking
            await this.#shuffleCardsSequential();

            // Transition to unlocked state
            await this.#transitionToUnlocked();

            // Hide form
            form.hidden = true;

            // Show cursor hint
            this.#showCursorHint();
        } catch (error) {
            console.error('Form submission failed:', error);

            // Reset loading state on error
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.classList.remove('mystery-prize-cards__submit--loading');
            }

            // Announce error
            this.#announce('Something went wrong. Please try again.');
        }
    }

    /**
     * Submit form data to backend
     * @returns {Promise<void>}
     */
    async #submitFormData() {
        const { form } = this.refs;
        if (!form) return;

        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());

        // Map form fields to expected JSON keys
        const payload = {
            first_name: data['contact[first_name]'],
            email: data['contact[email]']
        };

        try {
            // REPLACE WITH YOUR ACTUAL SUPABASE URL
            const SUPABASE_URL = 'https://brhqtnymecdonkrbwsqr.supabase.co/functions/v1/shopify-subscribe';

            const response = await fetch(SUPABASE_URL, {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if you set up RLS/Auth, otherwise for public functions it might be fine or use Anon key
                    'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJyaHF0bnltZWNkb25rcmJ3c3FyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0ODk0OTIsImV4cCI6MjA4MjA2NTQ5Mn0.eUdv95sal3z0TLWtIx8XjKDD46H5BfH6gmKeu6qsUpA'
                }
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
        } catch (e) {
            console.error('Submission error:', e);
            throw e;
        }
    }

    /**
     * Transition cards from locked to unlocked state
     * @returns {Promise<void>}
     */
    async #transitionToUnlocked() {
        this.#state = 'unlocked';

        const { cards, cardsGrid } = this.refs;
        if (!cards || !cardsGrid) return;

        // Update grid state
        cardsGrid.dataset.state = 'unlocked';

        // Update headers
        this.#updateHeaders('unlocked');

        // "THE DEAL" Animation - Cards fly out from deck
        // First, stack all cards to center (visually reset)
        for (const card of cards) {
            card.style.transition = 'none';
            card.style.transform = 'translateX(0) translateY(0) rotate(0deg) scale(0.9)';
            card.style.opacity = '0.7';
        }
        await this.#wait(50); // Allow style application

        // Now, animate each card flying to its fanned position with a stagger
        for (const [i, card] of cards.entries()) {
            card.classList.remove('mystery-prize-cards__card--locked');
            card.classList.add('mystery-prize-cards__card--unlocking');

            // Enable transitions
            card.style.transition = 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease-out';

            // Reset inline styles to allow CSS fan positioning to take over
            card.style.transform = '';
            card.style.opacity = '1';

            // Make card interactive
            card.setAttribute('role', 'button');
            card.setAttribute('tabindex', '0');

            await this.#wait(50 + i * 30); // Faster stagger

            card.classList.remove('mystery-prize-cards__card--unlocking');
            card.classList.add('mystery-prize-cards__card--unlocked');
        }

        // Clean up inline styles after animation
        await this.#wait(400);
        for (const card of cards) {
            card.style.transition = '';
        }

        // Announce state change
        this.#announce('Cards unlocked! Click or press Enter on a card to reveal your prize.');

        // Move focus to first card for keyboard users
        cards[0]?.focus();
    }

    /**
     * Update headers based on state
     * @param {CardState} state
     */
    #updateHeaders(state) {
        const headers = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.mystery-prize-cards__header'));
        for (const header of headers) {
            const headerState = header.dataset.state;
            header.hidden = headerState !== state;
        }
    }

    /**
     * Show the cursor hint
     */
    #showCursorHint() {
        const { cursorHint } = this.refs;
        if (!cursorHint) return;

        if (prefersReducedMotion()) return;

        cursorHint.hidden = false;
    }

    /**
     * Handle mouse move to update cursor position
     * @param {MouseEvent} event
     */
    #onMouseMove(event) {
        const { cursorHint } = this.refs;
        if (!cursorHint || this.#state !== 'unlocked' || cursorHint.hidden) return;

        const x = event.clientX;
        const y = event.clientY;

        cursorHint.style.setProperty('--cursor-left', `${x}px`);
        cursorHint.style.setProperty('--cursor-top', `${y}px`);
    }

    /**
     * Select a card and reveal the prize
     * @param {number} index
     */
    #selectCard(index) {
        if (this.#state !== 'unlocked') return;

        const { cards, cursorHint } = this.refs;
        const selectedCard = cards?.[index];
        if (!selectedCard) return;

        if (cursorHint) cursorHint.hidden = true;

        // --- RIGGED LOGIC START ---
        // We assume Card 5 (index 4) is the "Winner" configured in block settings.
        // 1. Get all revealed contents
        const allContents = cards.map(c => c.querySelector('.mystery-prize-cards__card-face--revealed')?.innerHTML ?? '');
        const allLinks = cards.map(c => c.dataset.link ?? '');

        // 2. Identify Winner Content (Index 4) & Loser Contents (0-3)
        const winnerContent = allContents[4] ?? '';
        const winnerLink = allLinks[4] ?? '';
        const loserContents = allContents.slice(0, 4);
        const loserLinks = allLinks.slice(0, 4);

        // 3. Assign Winner Content to Selected Card
        const selectedFace = selectedCard.querySelector('.mystery-prize-cards__card-face--revealed');
        if (selectedFace) {
            selectedFace.innerHTML = winnerContent;
        }
        selectedCard.dataset.link = winnerLink;
        this.#selectedLink = winnerLink;

        // 4. Assign Loser Contents to Other Cards
        let loserIndex = 0;
        for (let i = 0; i < cards.length; i++) {
            if (i === index) continue;

            const card = cards[i];
            if (!card) continue;

            const face = card.querySelector('.mystery-prize-cards__card-face--revealed');
            if (face && loserContents[loserIndex]) {
                face.innerHTML = loserContents[loserIndex];
            }

            if (loserLinks[loserIndex]) {
                card.dataset.link = loserLinks[loserIndex];
            }

            loserIndex++;
        }
        // --- RIGGED LOGIC END ---

        this.#revealCard(selectedCard);
    }

    /**
     * Reveal a card with animation
     * @param {HTMLElement} card
     * @returns {Promise<void>}
     */
    async #revealCard(card) {
        this.#state = 'revealed';
        const { cards, cardsGrid } = this.refs;

        if (cardsGrid) cardsGrid.dataset.state = 'revealed';
        this.#updateHeaders('revealed');

        // 1. Identify others
        const otherCards = cards.filter(c => c !== card);

        // 2. "THE SHOWDOWN" - Winner Fly-In
        // Remove unlocked class, add winner class
        // We let CSS definitions handle the transitions for smoother performance
        card.classList.remove('mystery-prize-cards__card--unlocked');
        card.classList.add('mystery-prize-cards__card--winner');

        // Force reflow and trigger animation
        void card.offsetWidth;
        card.classList.add('mystery-prize-cards__card--revealed');
        card.removeAttribute('role');
        card.removeAttribute('tabindex');

        // Announce win
        this.#announce('Congratulations! You won a prize!');

        // 3. Wait for winner reveal (snappier)
        await this.#wait(400);

        // 4. "LOSER CASCADE" - Phase 1: Move to positions
        // Randomize order for display
        const shuffledLosers = otherCards.sort(() => Math.random() - 0.5);

        // Apply positioning classes first (they move to bottom)
        for (const [i, c] of shuffledLosers.entries()) {
            c.classList.remove('mystery-prize-cards__card--unlocked');
            c.classList.add('mystery-prize-cards__card--loser');
            c.classList.add(`mystery-prize-cards__card--loser-${i + 1}`);
            c.removeAttribute('role');
            c.removeAttribute('tabindex');
        }

        // Wait for movement to finish
        await this.#wait(600);

        // Phase 2: Sequential Reveal (Flip)
        for (const c of shuffledLosers) {
            c.classList.add('mystery-prize-cards__card--revealed');
            await this.#wait(600); // Long delay to show "Oh, that was a bad card"
        }

        // 5. Show the CTA result button
        this.#showResult();
    }

    /**
     * Show the result section
     */
    #showResult() {
        const { result, ctaButton } = this.refs;
        if (!result) return;

        // Update CTA link if available
        if (ctaButton && this.#selectedLink) {
            ctaButton.href = this.#selectedLink;
        }

        result.hidden = false;

        requestAnimationFrame(() => {
            result.classList.add('mystery-prize-cards__result--visible');
        });

        result.focus?.();
    }

    /**
     * Announce a message to screen readers
     * @param {string} message
     */
    #announce(message) {
        const { liveRegion } = this.refs;
        if (liveRegion) {
            liveRegion.textContent = message;
        }
    }

    /**
     * Promise-based wait utility
     * @param {number} ms - Milliseconds to wait
     * @returns {Promise<void>}
     */
    #wait(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}

// Register custom element
if (!customElements.get('mystery-prize-cards-component')) {
    customElements.define('mystery-prize-cards-component', MysteryPrizeCards);
}
